import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const COUNTRY_NAME_MAX_LENGTH = 100;
export const COUNTRY_DESCRIPTION_MAX_LENGTH = 255;

export type CountryListRow = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  displayOrder: number;
};

export type CountryDetail = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  displayOrder: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type CreateCountryInput = {
  name: string;
  description?: string;
  active: boolean;
};

export type UpdateCountryInput = CreateCountryInput;

type ApiCountryRow = Record<string, unknown>;

function mapCountryListRow(row: ApiCountryRow): CountryListRow | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(row.code),
    description: str(row.description),
    active: bool(row.active) ?? true,
    displayOrder:
      typeof row.display_order === 'number'
        ? row.display_order
        : typeof row.displayOrder === 'number'
          ? row.displayOrder
          : 0,
  };
}

function mapCountryDetail(row: ApiCountryRow): CountryDetail | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(row.code),
    description: str(row.description),
    active: bool(row.active) ?? true,
    displayOrder:
      typeof row.display_order === 'number'
        ? row.display_order
        : typeof row.displayOrder === 'number'
          ? row.displayOrder
          : 0,
    createdAt: str(row.created_at ?? row.createdAt),
    createdBy: str(row.created_by ?? row.createdBy),
    updatedAt: str(row.updated_at ?? row.updatedAt),
    updatedBy: str(row.updated_by ?? row.updatedBy),
  };
}

function unwrapRows(data: unknown): ApiCountryRow[] {
  if (Array.isArray(data)) return data as ApiCountryRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.content)) return record.content as ApiCountryRow[];
  if (Array.isArray(record.data)) return record.data as ApiCountryRow[];
  return [];
}

/** GET /v1/masters/countries — CMS-MCO-M-4 */
export async function listCountries(): Promise<ApiResult<CountryListRow[]>> {
  const res = await caseApiRequest<unknown>('/v1/masters/countries', {
    method: 'GET',
  });
  if (!res.ok) return { success: false, message: res.message };
  const rows = unwrapRows(res.data)
    .map(mapCountryListRow)
    .filter((row): row is CountryListRow => row != null);
  return { success: true, data: rows };
}

/** GET /v1/masters/countries/{countryId} — CMS-MCO-M-2 */
export async function getCountryById(id: string): Promise<ApiResult<CountryDetail>> {
  const countryId = id.trim();
  if (!countryId) return { success: false, message: 'Country id is missing.' };
  const res = await caseApiRequest<unknown>(
    `/v1/masters/countries/${encodeURIComponent(countryId)}`,
    { method: 'GET' },
  );
  if (!res.ok) return { success: false, message: res.message };
  const detail = mapCountryDetail(
    res.data && typeof res.data === 'object' ? (res.data as ApiCountryRow) : {},
  );
  if (!detail) return { success: false, message: 'Country details are unavailable.' };
  return { success: true, data: detail };
}

/** POST /v1/masters/countries — CMS-MCO-M-1 */
export async function createCountry(input: CreateCountryInput): Promise<ApiResult<void>> {
  const res = await caseApiRequest<unknown>('/v1/masters/countries', {
    method: 'POST',
    body: {
      name: input.name.trim(),
      description: input.description?.trim() ?? '',
      active: input.active,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** PUT /v1/masters/countries/{countryId} — CMS-MCO-M-3 */
export async function updateCountry(
  id: string,
  input: UpdateCountryInput,
): Promise<ApiResult<void>> {
  const countryId = id.trim();
  if (!countryId) return { success: false, message: 'Country id is missing.' };
  const res = await caseApiRequest<unknown>(
    `/v1/masters/countries/${encodeURIComponent(countryId)}`,
    {
      method: 'PUT',
      body: {
        name: input.name.trim(),
        description: input.description?.trim() ?? '',
        active: input.active,
      },
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}
